import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
  Search,
  SlidersHorizontal,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Copy,
  Share2,
  MessageCircle,
  FileText,
  History,
  Eye,
} from 'lucide-react';
import { generatePrintableCaseReport } from '../../utils/printReport';
import { logActivity } from '../../api';

export const DataTable = ({
  columns,
  data,
  onRowClick,
  onBulkAction,
  bulkActions = [],
  searchPlaceholder = 'Search...',
  showSearch = true,
  showFilters = true,
  selectable = true,
  showActions = true,
  pageSize = 10,
  className,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const handleCopyLink = (row) => {
    const url = `${window.location.origin}/cases/${row.id}`;
    navigator.clipboard.writeText(url);
    toast.success(`Shareable URL copied for ${row.refNumber}!`);
    logActivity({
      action: 'Case Link Copied',
      description: `Copied shareable link for case ${row.refNumber}`,
      case_id: row.id,
    });
  };

  const handleWhatsApp = (row) => {
    const origin = window.location.origin;
    const caseUrl = `${origin}/cases/${row.id}`;
    const cleanPhone = (row.applicantPhone || '').replace(/[^0-9]/g, '');
    const clientName = row.client?.name || row.clientName || 'Client';
    const msg = `🏢 *AD VENTURES - CASE VERIFICATION*\n📋 *Case Ref:* ${row.refNumber}\n👤 *Applicant:* ${row.applicantName}\n📍 *Address:* ${row.address || 'N/A'}\n🏛️ *Client:* ${clientName}\n🔗 *View Case:* ${caseUrl}`;
    const waUrl = cleanPhone
      ? `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(msg)}`
      : `https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
    toast.success(`WhatsApp share opened for ${row.refNumber}!`);
    logActivity({
      action: 'WhatsApp Shared',
      description: `Shared case ${row.refNumber} via WhatsApp`,
      case_id: row.id,
    });
  };

  const handlePdf = (row) => {
    generatePrintableCaseReport(row);
    toast.success(`Generated PDF report for ${row.refNumber}!`);
    logActivity({
      action: 'PDF Report Generated',
      description: `Generated PDF report for case ${row.refNumber}`,
      case_id: row.id,
    });
  };

  // Filter data based on search
  const filteredData = data.filter(row =>
    Object.values(row).some(value =>
      String(value).toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Paginate data
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRows(paginatedData.map(row => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id, checked) => {
    if (checked) {
      setSelectedRows(prev => [...prev, id]);
    } else {
      setSelectedRows(prev => prev.filter(rowId => rowId !== id));
    }
  };

  const isAllSelected = paginatedData.length > 0 && 
    paginatedData.every(row => selectedRows.includes(row.id));

  return (
    <div className={cn('space-y-4', className)} data-testid="data-table">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {showSearch && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 pl-10 bg-white border-slate-200"
                data-testid="table-search"
              />
            </div>
          )}
          {showFilters && (
            <Button
              variant="outline"
              size="sm"
              className="bg-white border-slate-200 gap-2"
              data-testid="table-filters-btn"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </Button>
          )}
        </div>

        {/* Bulk Actions */}
        {selectedRows.length > 0 && bulkActions.length > 0 && (
          <div className="flex items-center gap-2 animate-fade-in">
            <span className="text-sm text-slate-500">
              {selectedRows.length} selected
            </span>
            {bulkActions.map((action) => (
              <Button
                key={action.label}
                variant={action.variant || 'outline'}
                size="sm"
                onClick={() => onBulkAction?.(action.key, selectedRows)}
                className="gap-2"
                data-testid={`bulk-action-${action.key}`}
              >
                {action.icon && <action.icon className="w-4 h-4" />}
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="crm-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-200 hover:bg-transparent">
              {selectable && (
                <TableHead className="w-12">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
              )}
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={cn(
                    'text-xs font-semibold uppercase tracking-wider text-slate-500',
                    column.sortable && 'cursor-pointer select-none'
                  )}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="flex items-center gap-2">
                    {column.header}
                    {column.sortable && (
                      <span className="text-slate-500">
                        {sortConfig.key === column.key ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp className="w-3.5 h-3.5" />
                          ) : (
                            <ArrowDown className="w-3.5 h-3.5" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3.5 h-3.5" />
                        )}
                      </span>
                    )}
                  </div>
                </TableHead>
              ))}
              {showActions && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable ? 2 : 1)}
                  className="h-32 text-center text-slate-500"
                >
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row) => (
                <TableRow
                  key={row.id}
                  className={cn(
                    'border-slate-100 cursor-pointer',
                    selectedRows.includes(row.id) && 'bg-blue-50'
                  )}
                  onClick={() => onRowClick?.(row)}
                  data-testid={`table-row-${row.id}`}
                >
                  {selectable && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedRows.includes(row.id)}
                        onCheckedChange={(checked) => handleSelectRow(row.id, checked)}
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell key={column.key} className="py-4">
                      {column.render ? column.render(row) : row[column.key]}
                    </TableCell>
                  ))}
                  {showActions && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-8 h-8 text-slate-500 hover:text-slate-900"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white border-slate-200 w-52 shadow-lg rounded-lg">
                          {row.refNumber ? (
                            <>
                              <DropdownMenuItem
                                className="cursor-pointer text-xs font-medium gap-2 text-slate-700"
                                onClick={() => navigate(`/cases/${row.id}`)}
                              >
                                <Eye className="w-3.5 h-3.5 text-blue-600" />
                                View Case Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="cursor-pointer text-xs font-medium gap-2 text-slate-700"
                                onClick={() => handleCopyLink(row)}
                              >
                                <Copy className="w-3.5 h-3.5 text-indigo-600" />
                                Copy Shareable URL
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="cursor-pointer text-xs font-medium gap-2 text-slate-700"
                                onClick={() => handleWhatsApp(row)}
                              >
                                <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                                Share on WhatsApp
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="cursor-pointer text-xs font-medium gap-2 text-slate-700"
                                onClick={() => handlePdf(row)}
                              >
                                <FileText className="w-3.5 h-3.5 text-red-600" />
                                Print / PDF Report
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-slate-100" />
                              <DropdownMenuItem
                                className="cursor-pointer text-xs font-medium gap-2 text-purple-700"
                                onClick={() => navigate(`/cases/${row.id}/history`)}
                              >
                                <History className="w-3.5 h-3.5 text-purple-600" />
                                Case History & Audit
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <>
                              <DropdownMenuItem className="cursor-pointer text-xs">View Details</DropdownMenuItem>
                              <DropdownMenuItem className="cursor-pointer text-xs">Edit</DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">
          Showing {((currentPage - 1) * pageSize) + 1} to{' '}
          {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} results
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="w-8 h-8"
            data-testid="pagination-first"
          >
            <ChevronsLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentPage(prev => prev - 1)}
            disabled={currentPage === 1}
            className="w-8 h-8"
            data-testid="pagination-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="px-3 text-sm text-slate-500">
            Page {currentPage} of {totalPages || 1}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentPage(prev => prev + 1)}
            disabled={currentPage >= totalPages}
            className="w-8 h-8"
            data-testid="pagination-next"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage >= totalPages}
            className="w-8 h-8"
            data-testid="pagination-last"
          >
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
